<script setup lang="ts">
import type { MaskInputOptions } from "maska";

import { watchDebounced } from "@vueuse/core";
import { vMaska } from "maska/vue";
import { computed, onMounted, reactive, ref, useCssModule, watch } from "vue";

import Modal from "@/components/modal/modal.vue";
import {
    type ParcelMachine,
    apiGetParcelMachines,
} from "@/hooks/requests/apiGetParcelMachines/apiGetParcelMachines";
import {
    apiPostDeliveryAddress,
    ApiPostDeliveryAddressPayload,
} from "@/hooks/requests/apiPostDeliveryAddress/apiPostDeliveryAddress";
import Select from "@/ui/select/select.vue";
import { BugTrackerSection, useBugTracker } from "~/tiw/helpers/bugTracker";
import parseStringPrice from "~/tiw/helpers/parseStringPrice";
import WIDGET_ANALYTICS_EVENTS from "~/tiw/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import createApplication from "~/tiw/hooks/requests/createApplication/createApplication";
import { useAnalytics } from "~/tiw/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "~/tiw/hooks/useStoreResetter/useStoreResetter";
import useTranslate from "~/tiw/hooks/useTranslate/useTranslate";
import useValidate from "~/tiw/hooks/useValidate";
import { useChooseDeviceStore } from "~/tiw/stores/chooseDevice/chooseDevice";
import { useDeliveryStore } from "~/tiw/stores/delivery/delivery";
import { useHowYouRateDeviceStore } from "~/tiw/stores/howYouRateDevice/howYouRateDevice";
import { useSuccessfulEvaluationStore } from "~/tiw/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "~/tiw/stores/tiw/tiw";
import Button from "~/tiw/ui/button/button.vue";
import Checkbox from "~/tiw/ui/checkbox/checkbox.vue";
import Field from "~/tiw/ui/field/field.vue";
import Input from "~/tiw/ui/input/input.vue";

import { getDeliveryDays } from "./thanksYou";

const classes = useCssModule();

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.PICKUP_ADDRESS);
});

const TIWStore = useTIWStore();
const DeliveryStore = useDeliveryStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const ChooseDeviceStore = useChooseDeviceStore();

const isSameAddress = ref(true);
const loadingToNext = ref(false);

const selectedDeliveryType = ref<ApiPostDeliveryAddressPayload["deliveryType"]>("courier");
const unwatch = watch(selectedDeliveryType, async (deliveryType) => {
    if (deliveryType === "locker") {
        parcelMachines.value = await getParcelMachines({ search: parcelMachineInputValue.value });
        unwatch();
    }
});
const parcelMachines = ref<ParcelMachine[]>([]);
const isParcelMachinesLoading = ref(false);
async function getParcelMachines(params: { search: string }): Promise<ParcelMachine[]> {
    let result: ParcelMachine[] = [];

    try {
        const parcelMachinesResponse = await apiGetParcelMachines(params);
        result = parcelMachinesResponse.data;
    } catch (e) {
        console.error("error when posts adresses loading: ", e);
        useBugTracker("getting all posts", BugTrackerSection.ERROR, {
            message: e.message,
        });
    }

    return result;
}
const selectedParcelMachineName = ref("");
const selectedParcelMachineAddress = ref("");
const parcelMachineInputValue = ref("");
const MIN_SEARCH_LENGTH = 3;
const noParcelMachinesText = ref(useTranslate("tiw__thanksYou__writeCharacters"));
watchDebounced(
    parcelMachineInputValue,
    async (inputValue) => {
        const search = inputValue.trim().toLowerCase();

        if (search.length < MIN_SEARCH_LENGTH) {
            parcelMachines.value = [];
            noParcelMachinesText.value = useTranslate("tiw__thanksYou__writeCharacters");

            return;
        }

        isParcelMachinesLoading.value = true;
        noParcelMachinesText.value = useTranslate("tiw__thanksYou__pleaseWait");

        parcelMachines.value = await getParcelMachines({ search });

        isParcelMachinesLoading.value = false;
        noParcelMachinesText.value = "";
    },
    {
        debounce: 500,
    },
);

const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

function setArrivalDate(date: string) {
    DeliveryStore.deliveryInfo.arrivalDate = date;
}

async function onNext() {
    loadingToNext.value = true;

    try {
        await createApplication({
            device_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            ex_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            imei: ChooseDeviceStore.field_imei.value,
            locale: TIWStore.standardizedLocale,
            location: TIWStore.WIDGET_DATA.location,
            source: TIWStore.WIDGET_DATA.code,
            phone: SuccessfulEvaluationStore.individualFields.phone.replace(/\D+/g, ""), // TODO узнай, почему используется SuccessfulEvaluationStore, а не DeliveryStore
            city_name: SuccessfulEvaluationStore.individualFields.city,
            email: SuccessfulEvaluationStore.individualFields.email,
            first_name: SuccessfulEvaluationStore.individualFields.name,
            last_name: SuccessfulEvaluationStore.individualFields.surname,
            pickup_date: DeliveryStore.deliveryInfo.arrivalDate,
            contact_person: {
                address:
                    `${DeliveryStore.deliveryInfo.street} ${DeliveryStore.deliveryInfo.houseNumber}, apt. ${DeliveryStore.deliveryInfo.apartmentNumber}`.trim(),
                city_name: DeliveryStore.deliveryInfo.city,
                first_name: DeliveryStore.deliveryInfo.name,
                last_name: DeliveryStore.deliveryInfo.surname,
                phone: DeliveryStore.deliveryInfo.phone.replace(/\D+/g, ""),
                email: DeliveryStore.deliveryInfo.email.trim(),
            },
            grade: HowYouRateDeviceStore.registeringDeviceData.grade,
            brand: ChooseDeviceStore.brandName,
            model: ChooseDeviceStore.field_model.value.name,
            price: parseStringPrice(handedDevicePrice.value),
            address:
                selectedDeliveryType.value === "locker"
                    ? selectedParcelMachineName.value
                    : SuccessfulEvaluationStore.address,
        });

        const DELIVERY_SERVICE: ApiPostDeliveryAddressPayload["deliveryService"] = "in_post";
        await apiPostDeliveryAddress({
            token: TIWStore.WIDGET_DATA.token,
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            contactFirstName: DeliveryStore.deliveryInfo.name,
            contactLastName: DeliveryStore.deliveryInfo.surname,
            contactPhone: DeliveryStore.deliveryInfo.phone.replace(/\D+/g, ""),
            contactEmail: DeliveryStore.deliveryInfo.email.trim(),
            index: DeliveryStore.deliveryInfo.postalCode,
            city: DeliveryStore.deliveryInfo.city,
            street: DeliveryStore.deliveryInfo.street,
            houseNum: DeliveryStore.deliveryInfo.houseNumber,
            apartmentNum: DeliveryStore.deliveryInfo.apartmentNumber,
            deliveryService: DELIVERY_SERVICE,
            deliveryType: selectedDeliveryType.value,
            customField1:
                selectedDeliveryType.value === "locker" ? selectedParcelMachineName.value : null,
        });
    } catch (e) {
        useBugTracker(
            "Successful Evaluation --- [POST] Create Application",
            BugTrackerSection.ERROR,
            {
                message: e?.message,
            },
        );
        loadingToNext.value = false;
        return;
    }

    if (TIWStore.DISCOUNT_BY_COURIER) {
        TIWStore.INJECTED_TIW.current_api.onDrop();
        TIWStore.closeWidget();
        TIWStore.switchStep("chooseDevice");
        useStoreResetter();
    } else {
        if (TIWStore.IS_DISCOUNT_LATER) {
            TIWStore.switchStep("thanksForOrder");
        } else {
            TIWStore.switchStep("voucherSent");
        }
    }
}

const phoneMaskOptions = reactive<MaskInputOptions>({
    mask: "+48 (___) ___-___",
    tokens: {
        _: {
            pattern: /\d/,
        },
        A: {
            pattern: /[1-9]/,
        },
    },
    eager: true,
});

const arrivalDates = getDeliveryDays().map((el) => {
    return { date: el.date, dateLabel: el.label, timeStart: "08:00", timeEnd: "18:00" };
});

const howDeleteDataText = computed(() => {
    const IOS_DOC_LINK = "https://cdn.new-brz.net/public/static/Instrukcja_iPhone_PL.pdf";
    const ANDROID_DOC_LINK = "https://cdn.new-brz.net/public/static/Instrukcja_Android_PL.pdf";

    const isDeviceApple = ChooseDeviceStore.field_brand.value.name === "Apple";
    const linkToDoc = isDeviceApple ? IOS_DOC_LINK : ANDROID_DOC_LINK;

    return useTranslate("tiw__thanksYou__deleteYourData").replace("[[LINK]]", linkToDoc);
});

watch(
    isSameAddress,
    () => {
        if (isSameAddress.value) {
            DeliveryStore.deliveryInfo.name = SuccessfulEvaluationStore.individualFields.name;
            DeliveryStore.deliveryInfo.apartmentNumber =
                SuccessfulEvaluationStore.individualFields.apartmentNumber;
            DeliveryStore.deliveryInfo.city = SuccessfulEvaluationStore.individualFields.city;
            DeliveryStore.deliveryInfo.houseNumber =
                SuccessfulEvaluationStore.individualFields.houseNumber;
            DeliveryStore.deliveryInfo.phone = SuccessfulEvaluationStore.individualFields.phone;
            DeliveryStore.deliveryInfo.email = SuccessfulEvaluationStore.individualFields.email;
            DeliveryStore.deliveryInfo.street = SuccessfulEvaluationStore.individualFields.street;
            DeliveryStore.deliveryInfo.surname = SuccessfulEvaluationStore.individualFields.surname;
            DeliveryStore.deliveryInfo.postalCode =
                SuccessfulEvaluationStore.individualFields.postalCode;
        } else {
            DeliveryStore.deliveryInfo.name = "";
            DeliveryStore.deliveryInfo.apartmentNumber = "";
            DeliveryStore.deliveryInfo.city = "";
            DeliveryStore.deliveryInfo.houseNumber = "";
            DeliveryStore.deliveryInfo.phone = "";
            DeliveryStore.deliveryInfo.street = "";
            DeliveryStore.deliveryInfo.postalCode = "";
            DeliveryStore.deliveryInfo.surname = "";
        }
    },
    { immediate: true },
);

const phoneValidation = computed(() => {
    if (!DeliveryStore.deliveryInfo.phone) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "phone",
        value: DeliveryStore.deliveryInfo.phone,
    });
    return valiDate;
});
const emailValidation = computed(() => {
    if (!DeliveryStore.deliveryInfo.email) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "email",
        value: DeliveryStore.deliveryInfo.email,
    });
    return valiDate;
});

const isCanNext = computed(() => {
    if (selectedDeliveryType.value === "courier") {
        return DeliveryStore.isCanNext;
    } else {
        return Boolean(selectedParcelMachineName.value);
    }
});
</script>

<template>
    <Modal class="tiw__step__thanksYou" :class="classes.modal">
        <template #header>
            <div :class="classes.headerContainer">
                <img src="\img\success.svg" alt="check icon" width="46px" />
                <div :class="classes.textsContainer">
                    <b>
                        {{ useTranslate("tiw__thanksYou__title") }}
                    </b>
                    <p>
                        {{ useTranslate("tiw__thanksYou__text") }}
                    </p>
                </div>
            </div>
        </template>
        <div class="tiw__step__thanksYou__body">
            <b>{{ useTranslate("tiw__thanksYou__deliveryMethod") }}</b>
            <form :class="classes.radioForm">
                <label :class="classes.radio">
                    <input
                        type="radio"
                        id="courier"
                        value="courier"
                        v-model="selectedDeliveryType"
                    />
                    <span :class="classes.radioView"></span>
                    {{ useTranslate("tiw__thanksYou__deliveryMethod__courier") }}
                </label>
                <label :class="classes.radio">
                    <input type="radio" id="locker" value="locker" v-model="selectedDeliveryType" />
                    <span :class="classes.radioView"></span>
                    {{ useTranslate("tiw__thanksYou__deliveryMethod__post") }}
                </label>
            </form>

            <template v-if="selectedDeliveryType === 'courier'">
                <b
                    ><p>{{ useTranslate("tiw__thanksYou__pleaseProvide") }}</p></b
                >
                <Checkbox
                    v-model="isSameAddress"
                    class="tiw__step__thanksYou__sameAddress"
                    :checked="isSameAddress"
                >
                    {{ useTranslate("tiw__thanksYou_useTheSameAddress") }}
                </Checkbox>
                <form>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__name__recipient") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.name"
                                type="text"
                                name="recipientName"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__surname__recipient") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.surname"
                                type="text"
                                name="recipientSurname"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__postalCode") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.postalCode"
                                type="text"
                                name="postalCode"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__city") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.city"
                                type="text"
                                name="city"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__street") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.street"
                                type="text"
                                name="street"
                            />
                        </template>
                        <template #hintBottom>
                            <span>
                                <!-- {{ docCityNameValidation.message }} -->
                            </span>
                        </template>
                    </Field>
                    <div class="tiw__step__thanksYou__body__houseApartment">
                        <Field>
                            <template #hintTop>
                                <span>
                                    {{ useTranslate("tiw__forms__houseNumber") }}
                                </span>
                            </template>
                            <template #body>
                                <Input
                                    v-model="DeliveryStore.deliveryInfo.houseNumber"
                                    type="text"
                                    name="houseNumber"
                                />
                            </template>
                            <template #hintBottom>
                                <span>
                                    <!-- {{ docCityNameValidation.message }} -->
                                </span>
                            </template>
                        </Field>
                        <Field>
                            <template #hintTop>
                                <span>
                                    {{ useTranslate("tiw__forms__apartmentNumber") }}
                                </span>
                            </template>
                            <template #body>
                                <Input
                                    v-model="DeliveryStore.deliveryInfo.apartmentNumber"
                                    type="text"
                                    name="apartmentNumber"
                                />
                            </template>
                            <template #hintBottom>
                                <span>
                                    <!-- {{ docCityNameValidation.message }} -->
                                </span>
                            </template>
                        </Field>
                    </div>
                    <Field :is-error="!phoneValidation.status">
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__phone") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.phone"
                                v-maska="phoneMaskOptions"
                                type="text"
                                name="recipientPhone"
                                :placeholder="phoneMaskOptions.mask"
                            />
                        </template>
                        <template v-if="!phoneValidation.status" #hintBottom>
                            <span>
                                {{ phoneValidation.message }}
                            </span>
                        </template>
                    </Field>
                    <Field :is-error="!emailValidation.status">
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__email") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="DeliveryStore.deliveryInfo.email"
                                type="text"
                                name="email"
                            />
                        </template>
                        <template v-if="!phoneValidation.status" #hintBottom>
                            <span>
                                {{ phoneValidation.message }}
                            </span>
                        </template>
                    </Field>
                </form>
                <p class="tiw__step__thanksYou__arrivalDate__title">
                    <b>{{ useTranslate("tiw__thanksYou_pleaseSpecifyArrivalDate") }}</b>
                </p>
                <div class="tiw__step__thanksYou__body__arrivalDate__items">
                    <div
                        v-for="arrivalDate in arrivalDates"
                        :key="arrivalDate.date"
                        :class="
                            arrivalDate.date === DeliveryStore.deliveryInfo.arrivalDate
                                ? 'tiw__step__thanksYou__body__arrivalDate__item__active'
                                : ''
                        "
                        class="tiw__step__thanksYou__body__arrivalDate__item"
                        @click="setArrivalDate(arrivalDate.date)"
                    >
                        <p class="tiw__step__thanksYou__body__arrivalDate__item__title">
                            {{ arrivalDate.dateLabel }}
                        </p>
                        <p class="tiw__step__thanksYou__body__arrivalDate__item__date">
                            {{ arrivalDate.timeStart }} - {{ arrivalDate.timeEnd }}
                        </p>
                    </div>
                </div>
            </template>

            <div v-else :class="classes.parcelMachinesContainer">
                <b>{{ useTranslate("tiw__thanksYou__postAddress") }}</b>
                <Select
                    v-model="selectedParcelMachineName"
                    :reduce="
                        (parcelMachine: ParcelMachine) => {
                            selectedParcelMachineAddress = parcelMachine.name_pl;
                            return parcelMachine.ref;
                        }
                    "
                    :options="parcelMachines"
                    @search="(query: string) => (parcelMachineInputValue = query)"
                    :filterable="false"
                    name="selectParcelMachine"
                    label="ref"
                    :is-loading="isParcelMachinesLoading"
                >
                    <template #option="{ ref, name_pl }">
                        <div>
                            <b>{{ `${useTranslate("tiw__thanksYou__inpostPaczkomat")} ${ref}` }}</b>
                            <p>{{ name_pl }}</p>
                        </div>
                    </template>

                    <template #selected-option="{ ref }">
                        <p>{{ `${useTranslate("tiw__thanksYou__inpostPaczkomat")} ${ref}` }}</p>
                    </template>

                    <template #no-options v-if="noParcelMachinesText">
                        {{ noParcelMachinesText }}
                    </template>
                </Select>

                <div v-if="selectedParcelMachineName" :class="classes.selectedParcelMachineCard">
                    <figure>
                        <img src="@/public/svg/post-adress.svg" alt="post icon" width="45px" />
                    </figure>

                    <div>
                        <b
                            >{{
                                `${useTranslate("tiw__thanksYou__inpostPaczkomat")} ${selectedParcelMachineName} `
                            }}
                        </b>
                        <p>{{ selectedParcelMachineAddress }}</p>
                    </div>
                </div>
            </div>
            <div :class="classes.howDeleteDataContainer">
                <img src="@/public/svg/shield-icon.svg" alt="shield icon" width="24px" />
                <p v-html="howDeleteDataText" :class="classes.text"></p>
            </div>
            <Button
                :disabled="!isCanNext || loadingToNext"
                class="tiw__step__thanksYou__body__buttonClose"
                @click="onNext"
            >
                {{ useTranslate("tiw__continue") }}
            </Button>
        </div>
    </Modal>
</template>

<style lang="scss" src="./thanksYou.scss"></style>
<style module>
.modal {
    --gap: 16px;
}
.headerContainer {
    background-color: #f5f5f5;
    border-radius: 6px;
    margin-top: 8px;
    padding: 15px 20px;
    display: grid;
    grid-template-columns: min-content 1fr;
    column-gap: 15px;

    .textsContainer {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
}
.parcelMachinesContainer {
    display: flex;
    flex-direction: column;
    gap: var(--gap);

    p {
        margin: 0;
    }
}
.selectedParcelMachineCard {
    display: flex;
    gap: 15px;
    padding: 20px;
    background-color: #f5f5f5;

    figure {
        margin: 0;
    }
}
.radioForm {
    margin: var(--gap) 0;
    display: flex;
    flex-direction: column;
    gap: var(--gap);
}
.radio {
    display: flex;
    gap: 12px;
    cursor: pointer;

    input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;

        &:checked + .radioView {
            border-color: black;
            border-width: 5px;
            background-color: white;
        }
    }

    .radioView {
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 1px solid #ccc;
        border-radius: 50%;
        box-sizing: border-box;
        transition: all 0.3s ease;
    }

    &:hover input:not(:checked) + .radioView {
        border-width: 2px;
        border-color: #999;
    }
}
.howDeleteDataContainer {
    display: flex;
    gap: 10px;
}
.text,
.text a {
    font-size: var(--custom-font-size);
    filter: grayscale(0.65);
}
</style>
